import type { ChatMessage } from 'models';
import { isMessageFilePayload, isMessageImagePayload, isMessageVideoPayload } from 'models';
import { type ReactNode, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from 'ui';

type P = {
  message?: ChatMessage;
};

export const useGetLastMessage = () => {
  const { t } = useTranslation();

  return useCallback(
    ({ message }: P): { icon?: ReactNode; text: string } => {
      if (!message) {
        return { text: '' };
      }

      if (message.text) {
        return { text: message.text };
      }

      const payloads = message.payload;
      if (payloads.length === 0) {
        return { text: '' };
      }

      const hasOnlyFiles = payloads.every((payload) => isMessageFilePayload(payload));
      const hasOnlyImages = payloads.every((payload) => isMessageImagePayload(payload));
      const hasOnlyVideos = payloads.every((payload) => isMessageVideoPayload(payload));

      if (hasOnlyFiles) {
        return { icon: <Icon icon="file-line" />, text: t('file', { count: payloads.length }) };
      }

      if (hasOnlyImages) {
        return { icon: <Icon icon="camera-2-line" />, text: t('photo', { count: payloads.length }) };
      }

      if (hasOnlyVideos) {
        return { icon: <Icon icon="camcorder-line" />, text: t('video', { count: payloads.length }) };
      }

      return { icon: <Icon icon="attachment-line" />, text: t('Multiple attachments') };
    },
    [t],
  );
};
